/**
 * MapQuickFilters — MAP-UX-1D.
 *
 * Chip-bar horizontal de filtros rápidos + counters operacionais, render-puro
 * dos campos ricos do DTO (MAP-DATA-1A/1B já mesclados em cada `device`).
 * Cada chip é AO MESMO TEMPO um counter (label · N) e um filtro: clicar filtra
 * o mapa por aquela categoria; clicar de novo (ou em "Todos") volta a tudo.
 *
 * NÃO altera proteção/comandos/bulk — é só visualização/navegação. Os matchers
 * são exportados p/ o MapView reusar na contagem E no filtro (DRY).
 */

// Predicados por categoria. `device` é o DTO enriquecido (ota_protected,
// secure_channel, auth_secret_set, clone_suspected, watchdog_enabled,
// has_pending_ota, target_release_version, fw_version, online…).
import { getProtectionState } from "../../lib/protection.js";

export const QUICK_MATCHERS = {
  online: (d) => !!d.online,
  offline: (d) => !d.online,
  protected: (d) => getProtectionState(d).protected,
  fw_stale: (d) => !!(
    d.fw_version && d.target_release_version &&
    d.fw_version !== d.target_release_version
  ),
  ota_pending: (d) => d.has_pending_ota === true,
  // HMAC inseguro = canal em claro COM secret definido (não conta device novo
  // pré-bootstrap, que tem auth_secret_set=false).
  hmac: (d) => d.secure_channel === false && d.auth_secret_set === true,
  clone: (d) => d.clone_suspected === true,
  watchdog_off: (d) => d.watchdog_enabled === false,
};
// "Atenção" = união dos sinais do badge de atenção do marcador (MAP-UX-1A).
QUICK_MATCHERS.attention = (d) =>
  QUICK_MATCHERS.hmac(d) || QUICK_MATCHERS.clone(d) ||
  QUICK_MATCHERS.watchdog_off(d) || QUICK_MATCHERS.ota_pending(d);

// Definição visual dos chips. `always` = sempre visível (mesmo count 0);
// os demais só aparecem quando count > 0 (mantém a barra compacta).
const CHIPS = [
  { key: "all", label: "Todos", always: true },
  { key: "online", label: "Online", always: true, color: "var(--ipwd-success)" },
  { key: "offline", label: "Offline", always: true, color: "var(--ipwd-sev-critical)" },
  { key: "protected", label: "Protegidos", color: "var(--ipwd-protected)" },
  { key: "fw_stale", label: "Fora do alvo", color: "var(--ipwd-sev-warning)" },
  { key: "ota_pending", label: "OTA pend.", color: "var(--ipwd-sev-warning)" },
  { key: "hmac", label: "HMAC inseg.", color: "var(--ipwd-sev-critical)" },
  { key: "clone", label: "Clone", color: "var(--ipwd-sev-critical)" },
  { key: "watchdog_off", label: "WD off", color: "var(--ipwd-sev-warning)" },
  { key: "attention", label: "Atenção", color: "var(--ipwd-sev-warning)" },
];

export default function MapQuickFilters({ counts, active, onChange }) {
  if (!counts) return null;
  return (
    <div className="ipwd-qf-bar" role="group" aria-label="Filtros rápidos da frota">
      {CHIPS.map((chip) => {
        const isAll = chip.key === "all";
        const n = isAll ? counts.all : (counts[chip.key] || 0);
        if (!chip.always && n === 0) return null;
        const isActive = isAll ? !active : active === chip.key;
        const style = isActive && chip.color
          ? { borderColor: chip.color, color: chip.color }
          : undefined;
        return (
          <button
            key={chip.key}
            type="button"
            className={"ipwd-qf-chip" + (isActive ? " is-active" : "")}
            style={style}
            aria-pressed={isActive}
            onClick={() => onChange(isAll ? null : (active === chip.key ? null : chip.key))}
          >
            {chip.color && !isAll && (
              <span className="ipwd-qf-dot" style={{ background: chip.color }} />
            )}
            <span>{chip.label}</span>
            <span className="ipwd-qf-count">{n}</span>
          </button>
        );
      })}
    </div>
  );
}
